/**
 * The folder watcher's policy, kept out of React so it can run under fake timers: upload every
 * found file (the addon file and the client caches) once at start, poll size and modified
 * time, wait for a changed file to settle,
 * hash it, skip unchanged bytes, space uploads to fit the server's hourly limit, back off on
 * rate limits and errors, freeze a rejected file until its bytes change, and pause on a lost
 * session. The page only reads snapshots and calls the control methods.
 */
import type { AddonUploadKind } from '@thottbot/shared';
import type { UploadOutcome } from '../api/addon-upload';
import type { FoundFile } from './discover';
import type { WatchTiming } from './types';

export const POLL_MS = 5000;
export const SETTLE_MS = 10_000;
/**
 * 2 min between uploads after the unspaced first batch: with the addon file and four cache
 * files, a session of reloads and logouts stays well inside the server's 30 uploads an hour.
 */
export const MIN_UPLOAD_SPACING_MS = 120_000;
export const BACKOFF_START_MS = 30_000;
export const BACKOFF_CAP_MS = 600_000;
export const DEFAULT_TIMING: WatchTiming = {
  pollMs: POLL_MS,
  settleMs: SETTLE_MS,
  spacingMs: MIN_UPLOAD_SPACING_MS,
};

export type WatchState = 'watching' | 'paused' | 'signed_out' | 'backing_off';
export type FilePhase = 'idle' | 'changed' | 'settling' | 'queued' | 'uploading' | 'rejected';
export type LogKind =
  'accepted' | 'duplicate' | 'skipped' | 'rejected' | 'rate_limited' | 'error' | 'unauthorized';

export interface WatchFileSnapshot {
  account: string;
  path: string;
  kind: AddonUploadKind;
  size: number | null;
  lastModified: number | null;
  phase: FilePhase;
  /** The last thing that happened to this file, in words. */
  lastResult: string | null;
}

export interface LogEntry {
  at: number;
  account: string;
  /** Which file the entry is about. */
  file: AddonUploadKind;
  kind: LogKind;
  message: string;
  uploadId?: string;
  rejectedCode?: string;
}

export interface WatchSnapshot {
  state: WatchState;
  lastCheckAt: number | null;
  /** When the next upload may start (spacing or back-off), if one is waiting. */
  nextUploadAt: number | null;
  files: WatchFileSnapshot[];
  log: LogEntry[];
  uploading: { account: string; progress: number } | null;
}

export interface WatcherDeps {
  files: FoundFile[];
  upload: (
    blob: Blob,
    file: FoundFile,
    onProgress?: (percent: number) => void,
  ) => Promise<UploadOutcome>;
  timing?: Partial<WatchTiming>;
  /** Content hash; the default is SHA-256 through Web Crypto. */
  hash?: (blob: Blob) => Promise<string>;
  now?: () => number;
}

export interface Watcher {
  start(): void;
  stop(): void;
  pause(): void;
  resume(): void;
  /** The page saw a valid session again after a 401. */
  sessionRestored(): void;
  subscribe(listener: () => void): () => void;
  snapshot(): WatchSnapshot;
}

const MAX_LOG = 100;

interface Tracked {
  found: FoundFile;
  size: number | null;
  lastModified: number | null;
  changedAt: number;
  /** A change (or the initial upload) has not been uploaded yet. */
  pending: boolean;
  /** The initial upload needs no settle wait. */
  initial: boolean;
  uploadedHash: string | null;
  rejectedHash: string | null;
  phase: FilePhase;
  lastResult: string | null;
}

interface Queued {
  tracked: Tracked;
  blob: Blob;
  hash: string;
  /** The upload at start: not spaced, so every account's file goes up right away. */
  initial: boolean;
}

async function sha256(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export function createWatcher(deps: WatcherDeps): Watcher {
  const timing: WatchTiming = { ...DEFAULT_TIMING, ...deps.timing };
  const now = deps.now ?? (() => Date.now());
  const hash = deps.hash ?? sha256;
  const tracked: Tracked[] = deps.files.map((found) => ({
    found,
    size: null,
    lastModified: null,
    changedAt: 0,
    pending: true,
    initial: true,
    uploadedHash: null,
    rejectedHash: null,
    phase: 'idle',
    lastResult: null,
  }));

  let state: WatchState = 'watching';
  let stopped = true;
  let lastCheckAt: number | null = null;
  let nextUploadAt: number | null = null;
  let backoffMs = 0;
  let uploading: { account: string; progress: number } | null = null;
  const log: LogEntry[] = [];
  const queue: Queued[] = [];
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let drainTimer: ReturnType<typeof setTimeout> | null = null;
  let ticking = false;
  const listeners = new Set<() => void>();
  let snap: WatchSnapshot | null = null;

  function emit(): void {
    snap = null;
    for (const l of listeners) l();
  }

  function addLog(t: Tracked, entry: Omit<LogEntry, 'at' | 'account' | 'file'>): void {
    log.unshift({ at: now(), account: t.found.account, file: t.found.kind, ...entry });
    if (log.length > MAX_LOG) log.length = MAX_LOG;
  }

  function enqueue(item: Queued): void {
    const i = queue.findIndex((q) => q.tracked === item.tracked);
    if (i >= 0) queue.splice(i, 1);
    queue.push(item);
    item.tracked.phase = 'queued';
  }

  async function check(t: Tracked): Promise<void> {
    const file = await t.found.handle.getFile();
    const changed = file.size !== t.size || file.lastModified !== t.lastModified;
    if (changed && t.size !== null) {
      t.pending = true;
      t.changedAt = now();
      t.phase = 'settling';
    }
    t.size = file.size;
    t.lastModified = file.lastModified;
    if (!t.pending || t.phase === 'queued' || t.phase === 'uploading') return;
    if (!t.initial && now() - t.changedAt < timing.settleMs) {
      t.phase = 'settling';
      return;
    }
    const digest = await hash(file);
    const initial = t.initial;
    t.initial = false;
    if (digest === t.uploadedHash) {
      t.pending = false;
      t.phase = 'idle';
      t.lastResult = 'unchanged, skipped';
      addLog(t, {
        kind: 'skipped',
        message: 'File rewritten with the same content; nothing to upload',
      });
      return;
    }
    if (digest === t.rejectedHash) {
      t.pending = false;
      t.phase = 'rejected';
      return;
    }
    enqueue({ tracked: t, blob: file, hash: digest, initial });
  }

  async function tick(): Promise<void> {
    if (stopped || ticking) return;
    ticking = true;
    try {
      for (const t of tracked) {
        try {
          await check(t);
        } catch (err) {
          t.lastResult = `Could not read the file: ${(err as Error).message}`;
        }
      }
      lastCheckAt = now();
    } finally {
      ticking = false;
    }
    emit();
    void drain();
  }

  function schedulePoll(): void {
    if (stopped) return;
    pollTimer = setTimeout(() => {
      pollTimer = null;
      void tick().then(schedulePoll);
    }, timing.pollMs);
  }

  function scheduleDrain(at: number): void {
    if (drainTimer) clearTimeout(drainTimer);
    drainTimer = setTimeout(
      () => {
        drainTimer = null;
        if (state === 'backing_off') state = 'watching';
        emit();
        void drain();
      },
      Math.max(0, at - now()),
    );
  }

  async function drain(): Promise<void> {
    if (stopped || uploading || queue.length === 0) return;
    if (state === 'paused' || state === 'signed_out') return;
    // Spacing and back-off gate everything except the first upload of each file at start.
    if (nextUploadAt !== null && now() < nextUploadAt && !(queue[0]!.initial && !inBackoff())) {
      if (!drainTimer) scheduleDrain(nextUploadAt);
      return;
    }
    const item = queue.shift()!;
    const t = item.tracked;
    t.phase = 'uploading';
    uploading = { account: t.found.account, progress: 0 };
    const startedAt = now();
    emit();
    let outcome: UploadOutcome;
    try {
      outcome = await deps.upload(item.blob, t.found, (percent) => {
        if (uploading) uploading.progress = percent;
        emit();
      });
    } catch (err) {
      outcome = { kind: 'error', message: (err as Error).message };
    }
    if (stopped) return;
    uploading = null;
    switch (outcome.kind) {
      case 'accepted':
      case 'duplicate':
        t.uploadedHash = item.hash;
        // A change that landed while this upload ran is still pending and settles as usual.
        t.pending = t.changedAt > startedAt;
        t.phase = t.pending ? 'settling' : 'idle';
        t.lastResult = outcome.kind === 'accepted' ? 'uploaded' : 'already uploaded';
        backoffMs = 0;
        nextUploadAt = now() + timing.spacingMs;
        addLog(t, {
          kind: outcome.kind,
          message: outcome.kind === 'accepted' ? 'Uploaded' : 'This exact file was uploaded before',
          uploadId: outcome.upload.id,
        });
        break;
      case 'rejected':
        t.rejectedHash = item.hash;
        t.pending = false;
        t.phase = 'rejected';
        t.lastResult = outcome.message;
        nextUploadAt = now() + timing.spacingMs;
        addLog(t, { kind: 'rejected', message: outcome.message, rejectedCode: outcome.code });
        break;
      case 'rate_limited':
        queue.unshift(item);
        t.phase = 'queued';
        nextUploadAt = now() + (outcome.retryAfterMs ?? BACKOFF_CAP_MS);
        state = 'backing_off';
        addLog(t, {
          kind: 'rate_limited',
          message: 'The site is rate-limiting uploads; retrying later',
        });
        break;
      case 'unauthorized':
        queue.unshift(item);
        t.phase = 'queued';
        state = 'signed_out';
        addLog(t, {
          kind: 'unauthorized',
          message: 'Your session has expired; sign in again to continue',
        });
        break;
      case 'error':
        queue.unshift(item);
        t.phase = 'queued';
        backoffMs = Math.min(BACKOFF_CAP_MS, backoffMs ? backoffMs * 2 : BACKOFF_START_MS);
        nextUploadAt = now() + backoffMs;
        state = 'backing_off';
        addLog(t, {
          kind: 'error',
          message: `${outcome.message}; retrying in ${Math.round(backoffMs / 1000)} s`,
        });
        break;
    }
    emit();
    if (queue.length && !uploadsHeld()) {
      const next = queue[0]!.initial && !inBackoff() ? now() : (nextUploadAt ?? now());
      scheduleDrain(next);
    }
  }

  // Read `state` through functions: inside `drain` TypeScript narrows it to the values the
  // switch assigned, which is not what the queue check needs.
  function uploadsHeld(): boolean {
    return state === 'paused' || state === 'signed_out';
  }
  function inBackoff(): boolean {
    return state === 'backing_off';
  }

  function clearTimers(): void {
    if (pollTimer) clearTimeout(pollTimer);
    if (drainTimer) clearTimeout(drainTimer);
    pollTimer = null;
    drainTimer = null;
  }

  return {
    start() {
      if (!stopped) return;
      stopped = false;
      void tick().then(schedulePoll);
    },
    stop() {
      stopped = true;
      clearTimers();
    },
    pause() {
      if (state === 'signed_out') return;
      state = 'paused';
      emit();
    },
    resume() {
      if (state !== 'paused') return;
      state = 'watching';
      emit();
      void drain();
    },
    sessionRestored() {
      if (state !== 'signed_out') return;
      state = 'watching';
      emit();
      void drain();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    snapshot() {
      if (!snap) {
        snap = {
          state,
          lastCheckAt,
          nextUploadAt: queue.length || state === 'backing_off' ? nextUploadAt : null,
          files: tracked.map((t) => ({
            account: t.found.account,
            path: t.found.path,
            kind: t.found.kind,
            size: t.size,
            lastModified: t.lastModified,
            phase: t.phase,
            lastResult: t.lastResult,
          })),
          log: [...log],
          uploading: uploading ? { ...uploading } : null,
        };
      }
      return snap;
    },
  };
}
